//
// Copyright (c) 2021 Astute Global, Inc.
//

import AkLinkFormView from "./ui/akLinkFormView";
import { addLinkProtocolIfApplicable, CssTransitionDisablerMixin, LinkUI } from "ckeditor5";
import {
  ATTRIBUTE_CONTENT_MAPPING,
  ATTRIBUTE_LINK_TOPIC,
  ATTRIBUTE_LINK_TYPE,
  buildTopicUrl,
  LINK_TYPE,
} from "./util";

export default class AkLinkUi extends LinkUI {
  _closeFormView() {
    const linkCommand = this.editor.commands.get("link");
    const linkHref = linkCommand.value;
    // Restore all information from the current selection
    linkCommand.refresh();
    if (linkHref !== undefined) {
      this._removeFormView();
    } else {
      this._hideUI();
    }
  }

  _createFormView() {
    const editor = this.editor;
    const t = editor.t;
    const linkCommand = editor.commands.get("link");
    const skipTopicLink = this.editor.config.get("link.topicLink.skip");
    const skipContentMappingLink = this.editor.config.get("link.contentMappingLink.skip");

    const formView = new (CssTransitionDisablerMixin(AkLinkFormView))(
      editor.locale,
      this._getFormValidators(editor),
    );

    const filteredLinkTypes = Object.keys(LINK_TYPE)
      .filter((key) => {
        if (skipTopicLink && LINK_TYPE[key] === LINK_TYPE.TOPIC_LINK) {
          return false;
        }
        return !(skipContentMappingLink && LINK_TYPE[key] === LINK_TYPE.CONTENT_MAPPING);
      })
      .reduce((acc, key) => {
        acc[key] = LINK_TYPE[key];
        return acc;
      }, {});

    formView.set({
      [ATTRIBUTE_CONTENT_MAPPING]: linkCommand[ATTRIBUTE_CONTENT_MAPPING],
      [ATTRIBUTE_LINK_TOPIC]: linkCommand[ATTRIBUTE_LINK_TOPIC],
      kbId: editor.config.get("ak.kbId"),
      languageId: editor.config.get("languageId"),
      [ATTRIBUTE_LINK_TYPE]: linkCommand[ATTRIBUTE_LINK_TYPE] || LINK_TYPE.URL,
      linkTypes: filteredLinkTypes,
      touchPointId: editor.config.get("touchPointId"),
    });
    formView.bind(ATTRIBUTE_CONTENT_MAPPING).to(linkCommand);
    formView.bind(ATTRIBUTE_LINK_TOPIC).to(linkCommand);
    formView.bind(ATTRIBUTE_LINK_TYPE).to(linkCommand);
    formView.displayedTextInputView
      .bind("isEnabled")
      .to(this, "selectedLinkableText", (value) => value !== undefined);
    formView.urlInputView.fieldView.bind("value").to(linkCommand);

    // Form elements should be read-only when corresponding commands are disabled.
    formView.urlInputView
      .bind("isEnabled")
      .to(
        linkCommand,
        "isEnabled",
        formView,
        ATTRIBUTE_LINK_TYPE,
        (value, linkType) => value && linkType === LINK_TYPE.URL,
      );
    formView.saveButtonView.bind("isEnabled").to(linkCommand);

    // Change the "Save" button label depending on the command state.
    formView.saveButtonView
      .bind("label")
      .to(linkCommand, "value", (value) => (value ? t("Update") : t("Insert")));

    // Execute link command after clicking the "Save" button.
    this.listenTo(formView, "submit", () => this._onFormViewSubmit(formView, linkCommand));

    // Update balloon position when form error changes.
    this.listenTo(formView.urlInputView, "change:errorText", () => {
      editor.ui.update();
    });

    // Hide the panel after clicking the "Cancel" button.
    this.listenTo(formView, "cancel", () => {
      this._closeFormView();
    });

    // Close the panel on esc key press when the **form has focus**.
    formView.keystrokes.set("Esc", (_data, cancel) => {
      this._closeFormView();
      cancel();
    });
    // Watch adding new link providers and add them to the buttons list.
    formView.providersListChildren
      .bindTo(this._linksProviders)
      .using((provider) => this._createLinksListProviderButton(provider));

    return formView;
  }
  /**
   * Obtains the state of the manual decorators.
   */
  _getDecoratorSwitchesState(formView) {
    const linkCommand = this.editor.commands.get("link");
    const linkType = formView ? formView[ATTRIBUTE_LINK_TYPE] : linkCommand[ATTRIBUTE_LINK_TYPE];
    let state = {
      [ATTRIBUTE_LINK_TYPE]: linkType,
      [ATTRIBUTE_CONTENT_MAPPING]: undefined,
      [ATTRIBUTE_LINK_TOPIC]: undefined,
    };
    if (linkCommand.manualDecorators) {
      state = Array.from(linkCommand.manualDecorators).reduce((accumulator, manualDecorator) => {
        const value =
          linkCommand.value === undefined && manualDecorator.value === undefined
            ? manualDecorator.defaultValue
            : manualDecorator.value;
        return {
          ...accumulator,
          [manualDecorator.id]: !!value,
        };
      }, state);
    }

    switch (linkType) {
      case LINK_TYPE.CONTENT_MAPPING:
        state[ATTRIBUTE_CONTENT_MAPPING] = formView
          ? JSON.stringify({
              id: formView.contentMappingView.fieldView.contentMappingId,
              name:
                formView.contentMappingView.fieldView.contentMappingName ||
                formView.contentMappingView.fieldView.contentMappingId,
            })
          : linkCommand[ATTRIBUTE_CONTENT_MAPPING];
        break;
      case LINK_TYPE.TOPIC_LINK:
        state[ATTRIBUTE_LINK_TOPIC] = formView
          ? JSON.stringify({
              id: formView.topicLinkView.fieldView.topicId,
              label:
                formView.topicLinkView.fieldView.topicLabel ||
                formView.topicLinkView.fieldView.topicId,
            })
          : linkCommand[ATTRIBUTE_LINK_TOPIC];
        state.linkOpenInNewTab = false;
        break;
    }

    return state;
  }
  /**
   * Returns link form validation callbacks.
   *
   * @param editor Editor instance.
   */
  _getFormValidators(editor) {
    const t = editor.t;
    const allowCreatingEmptyLinks = editor.config.get("link.allowCreatingEmptyLinks");
    return [
      (form) => {
        if (!allowCreatingEmptyLinks && !form.url.length) {
          return t("Link URL must not be empty.");
        }
      },
    ];
  }

  _onFormViewSubmit(formView) {
    if (!formView.isValid()) {
      return;
    }
    const editor = this.editor;
    const defaultProtocol = editor.config.get("link.defaultProtocol");
    const kbId = editor.config.get("ak.kbId");
    const decoratorSwitchesState = this._getDecoratorSwitchesState(formView);
    const displayedText = formView.displayedTextInputView.fieldView.element.value;
    const linkType = decoratorSwitchesState[ATTRIBUTE_LINK_TYPE];
    let value = formView.url;
    if (!value) {
      const linkCommand = this.editor.commands.get("link");
      value = linkCommand.value;
    }
    let parsedUrl;
    switch (linkType) {
      case LINK_TYPE.TOPIC_LINK:
        parsedUrl = buildTopicUrl(kbId, decoratorSwitchesState[ATTRIBUTE_LINK_TOPIC]);
        break;
      case LINK_TYPE.URL:
        parsedUrl = addLinkProtocolIfApplicable(value, defaultProtocol);
        break;
      default:
        parsedUrl = value;
        break;
    }

    editor.execute(
      "link",
      parsedUrl,
      decoratorSwitchesState,
      displayedText !== this.selectedLinkableText ? displayedText : undefined,
    );
    this._closeFormView();
  }
}
